import { SUPPORTED_LOCALES } from "@outschool/localization";
import { getIdFromSlug } from "@outschool/routes";

export const CDP_LAYOUT_EXPERIMENT_NAME = "CDPLayoutExperiment_fix_2";

const CDP_BASE_SEGMENTS = ["classes", "groups"];

/**
 * Sub-routes with their own entry ahead of the class details route in the
 * client route table. Everything else falls through to that route and keeps
 * the class details body mounted, so this has to list what the route table
 * intercepts rather than what the class details page serves.
 *
 * It is the union over both base segments, so a few combinations that nothing
 * links to, `/groups/:uid/posts` among them, are excluded without an entry of
 * their own. A test keeps the union in step with the route table.
 */
export const CDP_INTERCEPTED_SUB_SEGMENTS = [
  "edit",
  "schedule",
  "posts",
  "availability",
  "approval",
  "feedback",
  "syllabus",
  "admin",
  "manage",
];

/**
 * Identified by the slug id rather than the path: the canonical redirect
 * rewrites the title half of the slug and leaves the id.
 */
export function classDetailsPageId(pathname: string): string | undefined {
  const segments = pathname.split("/").filter(Boolean);
  const hasLocalePrefix = SUPPORTED_LOCALES.some(
    locale => locale.toLowerCase() === segments[0]?.toLowerCase(),
  );
  const [base, slug, ...rest] = hasLocalePrefix ? segments.slice(1) : segments;

  // Express and react-router both match case insensitively, so the segments
  // are compared folded. A slug id is base64 and has to keep its own case.
  if (
    !CDP_BASE_SEGMENTS.includes(base?.toLowerCase()) ||
    !slug ||
    slug.toLowerCase() === "create"
  ) {
    return undefined;
  }

  if (
    rest.length > 0 &&
    CDP_INTERCEPTED_SUB_SEGMENTS.includes(rest[0].toLowerCase())
  ) {
    return undefined;
  }

  return getIdFromSlug(slug) || undefined;
}

export function isClassDetailsPagePath(pathname: string): boolean {
  return classDetailsPageId(pathname) !== undefined;
}
